import DatePicker, {ReactDatePickerProps} from "react-datepicker";
import {Controller, FieldError, Path, RegisterOptions, useFormContext, FieldValues} from "react-hook-form";
import {getYear, addBusinessDays, format, startOfDay} from "date-fns";
import {ESBA_DATE_FORMAT} from "../../../common";
import {toBackendDateISO, parseBackendDateISO} from "common/util/date";
import {CustomDateInput} from "../../component/CustomDateInput";
import { ModalTooltip } from "common/component/ModalTooltip";
import React, {ReactElement} from "react";

type DatePickerProps<T extends FieldValues> = {
    label: string;
    disabled: boolean;
    required?: boolean;
    field: keyof T;
    dependentFields?: Array<keyof T> | undefined;
    optionalRules?: RegisterOptions;
    shouldShowErrorAtBottom?: ((errorType: string) => boolean);
    bottomError?: ((errorMsg: string) => ReactElement) | null;
    onChangeCallback?: (value?: string | null) => void;
    minYear?: number;
    maxYear?: number;
    maxBusinessDaysFromNow?: number;
    minBusinessDaysInPast?: number;
    containerClassName?: string;
    labelClassName?: string;
    datePickerWrapperClassName?: string;
    inputClassName?: string;
    stacked?: boolean; /** When true, label on one row and date picker on the row below. Default: false. */
    placeholder?: string;
    labelPosition?: 'left' | 'top';
    tooltipContent?: string;
} & Omit<ReactDatePickerProps, 'onChange'>;

export function ModalDatePicker<T extends FieldValues>({
                                       label,
                                       disabled,
                                       field,
                                       optionalRules,
                                       dependentFields,
                                       shouldShowErrorAtBottom = () => false,
                                       bottomError = null,
                                       required = true,
                                       onChangeCallback,
                                       minYear = 2000,
                                       maxYear = new Date().getFullYear() + 100,
                                       maxBusinessDaysFromNow,
                                       minBusinessDaysInPast,
                                       containerClassName = "w-full flex text-base items-center pb-2",
                                       labelClassName = "w-2/6 text-right",
                                       datePickerWrapperClassName = "w-3/6",
                                       inputClassName,
                                       stacked = false,
                                       placeholder,
                                       labelPosition = 'left',
                                       tooltipContent,
                                       ...datePickerProps
                                   }: DatePickerProps<T>) {
    const {
        control,
        formState,
        trigger,
    } = useFormContext<T>();
    const errors = formState.errors as Record<keyof T, FieldError>;
    const dependents = dependentFields || [];
    
    // Calculate max date based on business days from now
    const calculateMaxDate = (): Date | undefined => {
        if (!maxBusinessDaysFromNow) return undefined;
        return addBusinessDays(new Date(), maxBusinessDaysFromNow);
    };

    // Calculate min date based on business days in the past
    const calculateMinDate = (): Date | undefined => {
        if (!minBusinessDaysInPast) return undefined;
        return addBusinessDays(new Date(), -minBusinessDaysInPast);
    };
    
    const maxDate = calculateMaxDate();
    const minDate = calculateMinDate();
    const callerMaxDate = datePickerProps.maxDate as Date | undefined;
    
    // Create validation rules with year range and business days validation
    let rules: RegisterOptions = {
        required: {value: required, message: "Required"},
        validate: {
            validYear: (value: string) => {
                if (!value) return true; // Let required validation handle empty values
                try {
                    const date = parseBackendDateISO(value)!;
                    const year = getYear(date);
                    if (year < minYear || year > maxYear) {
                        return "Invalid date format";
                    }
                    return true;
                } catch {
                    return "Invalid date format";
                }
            },
            withinBusinessDays: (value: string) => {
                if (!value || !maxDate) return true;
                const selectedDate = parseBackendDateISO(value)!;
                const dateFormat = (datePickerProps.dateFormat as string) || ESBA_DATE_FORMAT;
                const formattedMaxDate = format(maxDate, dateFormat === 'MM/dd/yyyy' ? 'MM/dd/yyyy' : 'yyyy-MM-dd');
                
                if (selectedDate > maxDate) {
                    return `Date cannot exceed ${formattedMaxDate}`;
                }
                return true;
            },
            withinMinBusinessDays: (value: string) => {
                if (!value || !minDate) return true;
                const selectedDate = startOfDay(parseBackendDateISO(value)!);
                const minDateStart = startOfDay(minDate);
                const dateFormat = (datePickerProps.dateFormat as string) || ESBA_DATE_FORMAT;
                const formattedMinDate = format(minDateStart, dateFormat === 'MM/dd/yyyy' ? 'MM/dd/yyyy' : 'yyyy-MM-dd');
                
                if (selectedDate < minDateStart) {
                    return `${label} cannot be before ${formattedMinDate}`;
                }
                return true;
            },
            withinMaxDate: (value: string) => {
                if (!value || !callerMaxDate) return true;
                const selectedDate = parseBackendDateISO(value)!;
                const dateFormat = (datePickerProps.dateFormat as string) || ESBA_DATE_FORMAT;
                const formattedMaxDate = format(callerMaxDate, dateFormat === 'MM/dd/yyyy' ? 'MM/dd/yyyy' : 'yyyy-MM-dd');
                if (selectedDate > callerMaxDate) {
                    return `Date cannot exceed ${formattedMaxDate}`;
                }
                return true;
            }
        }
    };
    
    if (optionalRules) {
        // Merge custom rules with year validation
        rules = {
            ...optionalRules,
            validate: {
                ...rules.validate,
                ...optionalRules.validate
            }
        };
    }

    const isLabelTop = labelPosition === 'top';
    const isStacked = stacked || isLabelTop;

    let containerKlassName = containerClassName || '';
    let labelKlassName = labelClassName || '';
    let datePickerWrapperKlassName = datePickerWrapperClassName || '';
    
    if (isStacked) {
        if (containerKlassName.includes('items-center')) {
            containerKlassName = containerKlassName.replace('items-center', 'flex-col');
        } else {
            containerKlassName += ' flex-col';
        }

        labelKlassName = 'block mb-2';
        datePickerWrapperKlassName = 'w-full';
    }

    const renderDatePicker = () => (
        <Controller
            name={field as string as Path<T>}
            control={control as any}
            rules={rules}
            render={({field: {onChange, onBlur, value: isoValueStr}}) => <DatePicker
                todayButton="Today"
                wrapperClassName={datePickerWrapperKlassName}
                maxDate={maxDate}
                minDate={minDate}
                {...datePickerProps}
                disabled={disabled}
                placeholderText={placeholder}
                onSelect={(newValue: Date | null) => {
                    const isoValue = toBackendDateISO(newValue);
                    onChange(isoValue);
                    trigger(field as string as Path<T>);
                    dependents.forEach((field) => trigger(field as string as Path<T>));
                    onChangeCallback?.(isoValue);
                }}
                onChange={() => {}}
                onBlur={onBlur}
                selected={isoValueStr ? parseBackendDateISO(isoValueStr) : undefined}
                dateFormat={(datePickerProps.dateFormat as string) || ESBA_DATE_FORMAT}
                customInput={
                    <CustomDateInput
                        fieldName={field as string}
                        value={new Date().toISOString()}
                        marginL={isStacked ? '' : undefined}
                        fullWidth={isStacked}
                        inputClassName={`p-2 border rounded focus:outline-none focus:border-gray-500 ${
                    errors[field] ? 'border-red-500' : 'border-gray-300'
                } ${disabled ? '' : 'hover:border-atlantis-100'} ${isStacked ? 'w-full' : ''} ${inputClassName || ''}`}
                        placeholder={placeholder}
                        dateFormat={datePickerProps.dateFormat as string}
                        errorMsg={undefined} // Don't show error inline
                        onFinish={(newValue: Date | null) => {
                            const isoValue = toBackendDateISO(newValue);
                            onChange(isoValue);
                            trigger(field as string as Path<T>);
                            dependents.forEach((field) => trigger(field as string as Path<T>));
                            onChangeCallback?.(isoValue);
                        }}
                    />
                }
            />}
        />
    );

    const renderLabel = () => (
        <>
            {label}
            {tooltipContent && <ModalTooltip content={tooltipContent} />}
        </>
    );

    return (
        <>
            <div className={containerKlassName}>
                <div className={labelKlassName}>
                    {renderLabel()}
                </div>
                <div className={datePickerWrapperKlassName}>
                    {renderDatePicker()}
                    {errors[field] && !shouldShowErrorAtBottom(errors[field]?.type ?? '') && (
                        <div className={`text-red-700 mt-1 ${!isLabelTop ? 'pl-4' : ''}`}>
                            {errors[field].message}
                        </div>
                    )}
                </div>
            </div>
            {
                bottomError && shouldShowErrorAtBottom(errors[field]?.type ?? '') && errors[field] && bottomError(errors[field].message ?? '')
            }
        </>
    );
}
